import { Node, NodeConstructor } from "../../Node";
import { PString } from "../../properties/PString";
import { PNumber } from "../../properties/PNumber";
import { json } from "../../../global";
import { ThreeMixin } from "../ThreeMixin";
import { LightJson } from "../LightJson";
import { BasicShadowMap, LightShadow, PCFSoftShadowMap, VSMShadowMap } from "three";

export class ShadowNode extends ThreeMixin<NodeConstructor, LightShadow>(Node) {
  id: number;
  previousShadow3d: LightShadow;
  parent: LightJson;

  constructor(parentNode: LightJson) {
    super();
    this.parent = parentNode;
    // folgendes, damit die Vergleichswerte vorher/nachher initialisiert sind
    this.changed("json", () => this.toJSON());
    this.changed("Type");
  }

  // @ts-ignore
  get threeValue() {
    return this.parent.threeValue?.shadow;
  }

  set threeValue(v) {
    throw new Error("not intended");
  }

  fillWithDefaults() {
    throw new Error("not intended");
    return this;
  }

  fromJSON(json: json = {}, filter = (p) => p.type) {
    return super.fromJSON(json, filter);
  }

  fromThree(threeValue) {
    // if (threeValue!==this.threeValue) {
    //     debugger;
    // }
    return super.fromThree(threeValue);
  }

  toThree(threeValue) {
    // if (threeValue!==this.threeValue) {
    //     debugger;
    // }
    const props = this.getProperties().filter((p) => p.isThree !== undefined && p.isThree(p));
    props.forEach((p) => p.toThree(threeValue));
  }

  getProperties() {
    if (this._properties == null || this.previousShadow3d !== this.threeValue) {
      this._resetProperties();
      this.previousShadow3d = this.threeValue;
    }
    return this._properties;
  }

  updateScene() {
    this._dataChanged = {};
    const changedJson = this.changed("json", () => this.toJSON());
    if (changedJson) {
      this.parent.updateScene();
      this.threeValue.needsUpdate = true;
    }
  }

  /**
   * Dient der Strukturierung und Ergänzung vorhandener Properties, welche mittels Reflektion aus material3d ermittelt werden
   */
  static propertyGruppierung = [
    {
      name: "mapSize",
    },
    {
      name: "bias",
      propertyClass: PNumber,
      default: 0,
      decimals: 4,
      min: -0.01,
      max: 0.01,
      comment:
        "Shadow map bias, how much to add or subtract from the normalized depth when deciding whether a surface is in shadow.\n" +
        "The default is 0. Very tiny adjustments here (in the order of 0.0001) may help reduce artifacts in shadows",
    },
    {
      name: "normalBias",
      propertyClass: PNumber,
      default: 0,
      min: -2,
      max: 2,
      comment:
        "Defines how much the position used to query the shadow map is offset along the object normal. The default is 0. Increasing this value can be used to reduce shadow acne especially in large scenes where light shines onto geometry at a shallow angle. The cost is that shadows may appear distorted.",
    },
    {
      name: "radius",
      propertyClass: PNumber,
      default: 1,
      min: 0,
      max: 50,
      comment:
        "Setting this to values greater than 1 will blur the edges of the shadow.\n" +
        "High values will cause unwanted banding effects in the shadows - a greater mapSize will allow for a higher value to be used here before these effects become visible.\n" +
        "If WebGLRenderer.shadowMap.type is set to PCFSoftShadowMap, radius has no effect and it is recommended to increase softness by decreasing mapSize instead.\n" +
        "Note that this has no effect if the WebGLRenderer.shadowMap.type is set to BasicShadowMap.",
      showInEditor: (p) =>
        ![BasicShadowMap, PCFSoftShadowMap].includes(APP.renderer.shadowMap.type as any),
    },
    {
      name: "blurSamples",
      propertyClass: PNumber,
      default: 0,
      decimals: 0,
      min: 0,
      max: 40,
      comment: "The amount of samples to use when blurring a VSM shadow map.",
      showInEditor: (p) => VSMShadowMap === APP.renderer.shadowMap.type,
    },
    {
      name: "intensity",
      propertyClass: PNumber,
      default: 1,
      min: 0,
      max: 1,
      comment:
        "The intensity of the shadow. The default is 1. Valid values are in the range [0, 1].",
    },
  ];

  createProperties() {
    let properties = this.getGenericPropertiesFromThreejs(this.threeValue, (key, value) => {
      if (key == "mapSize") {
        return new (class extends PString {
          toThree(threeValue) {
            const s = parseInt(this.get() ?? "512", 10);
            threeValue.mapSize.set(s, s);
          }

          fromThree(threeValue) {
            this.set((threeValue.mapSize?.x ?? 512) + "");
          }
        })({
          name: "mapSize",
          selectOptions: _.range(2, 15).map((i) => Math.pow(2, i) + ""),
          comment:
            "Die Seitenlänge der ShadowMap. Higher values give better quality shadows at the cost of computation time. Values must be powers of 2, up to the WebGLRenderer.capabilities.maxTextureSize for a given device.",
          node: this,
        });
      }
      if (/^(autoUpdate|camera|map|mapPass|matrix|needsUpdate)$/g.test(key)) {
        return null;
      }
      return undefined;
    });
    return this.orderAndGroup(ShadowNode.propertyGruppierung, properties);
  }

  getTableName() {
    return "LightJson";
  }
}
